import type { AppLocale } from "@/lib/i18n/constants";
import { isContentField, isRecord } from "@/lib/content/schema";
import type { ContentEntry, ContentObject, DeepContentPartial } from "@/lib/content/types";
import { getApprovedSensitiveReviewOverride } from "./approved-sensitive-review-r2";

export type LocaleContentOverrides = Partial<Record<AppLocale, Record<string, DeepContentPartial<ContentObject>>>>;

function mergeApprovedSensitiveOverride(
  existing: DeepContentPartial<ContentObject> | undefined,
  approved: DeepContentPartial<ContentObject> | undefined,
  path: string,
): DeepContentPartial<ContentObject> | undefined {
  if (existing === undefined) return approved;
  if (approved === undefined) return existing;
  return mergeOverrideValue(existing, approved, path) as DeepContentPartial<ContentObject>;
}

function mergeOverrideValue(existing: unknown, approved: unknown, path: string): unknown {
  if (existing === undefined) return approved;
  if (approved === undefined) return existing;

  if (
    isContentField(existing)
    || isContentField(approved)
    || Array.isArray(existing)
    || Array.isArray(approved)
    || !isRecord(existing)
    || !isRecord(approved)
  ) {
    throw new Error(
      `Approved sensitive review overlay conflicts with an existing locale override at ${path}.`,
    );
  }

  const merged: Record<string, unknown> = { ...existing };
  for (const [key, value] of Object.entries(approved)) {
    merged[key] = mergeOverrideValue(merged[key], value, `${path}.${key}`);
  }
  return merged;
}

export function applyLocaleOverrides<TEntry extends ContentEntry>(
  entries: readonly TEntry[],
  localeOverrides: LocaleContentOverrides,
): TEntry[] {
  return entries.map((entry) => {
    const nextLocaleOverrides = { ...entry.localeOverrides };

    for (const [locale, overridesByKey] of Object.entries(localeOverrides) as [
      AppLocale,
      Record<string, DeepContentPartial<ContentObject>>,
    ][]) {
      const override = mergeApprovedSensitiveOverride(
        overridesByKey[entry.key],
        getApprovedSensitiveReviewOverride(entry, locale),
        `${locale}.${entry.key}`,
      );

      if (override) {
        nextLocaleOverrides[locale] = override as never;
      }
    }

    if (Object.keys(nextLocaleOverrides).length > 0) {
      (entry as { localeOverrides?: ContentEntry["localeOverrides"] }).localeOverrides = nextLocaleOverrides;
    }

    return entry;
  });
}
